import { useEffect, useState } from 'react';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { DetailSection, InstructorLegend, LabelText } from '../../../../../components/generic';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import {
  FormSelect,
  FormSelectOptions
} from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { Scopes, User } from '../../../../../services/endpoints/people/user';
import { useGetVenuesQuery } from '../../../../../services/endpoints/places/venue';
import { useGetLessonsQuery } from '../../../../../services/endpoints/schedule/lesson';
import { getCurrentUser } from '../../../../../services/helper';
import { Lesson } from '../../../../../types/lesson.type';
import { classColumns } from '../../../../../utils/consts/columnDefs/classColumns';
import { parseClassesDeepLinkFilters } from '../../../../../utils/listFilterSearchParams';
import {
  affirmativeOptions,
  courseTypeOptions,
  isOnlineOptions,
  paymentTypeOptions
} from '../sessions/SessionsView';

const defaultScope = 'upcoming';
const currentUser = getCurrentUser();

const dayOfWeekOptions = [
  {
    value: 'Sunday',
    label: 'Sunday'
  },
  {
    value: 'Monday',
    label: 'Monday'
  },
  {
    value: 'Tuesday',
    label: 'Tuesday'
  },
  {
    value: 'Wednesday',
    label: 'Wednesday'
  },
  {
    value: 'Thursday',
    label: 'Thursday'
  },
  {
    value: 'Friday',
    label: 'Friday'
  },
  {
    value: 'Saturday',
    label: 'Saturday'
  }
];

export const userCanView_Classes = (user: User | null) => {
  if (
    user?.isSuper ||
    user?.isSupremeAdmin ||
    user?.isAdmin ||
    user?.isGlobal ||
    user?.isLocationAdmin ||
    user?.isInstructor
  ) {
    return true;
  }

  return false;
};

export const ClassesView = () => {
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl(
    {
      scope: defaultScope,
      data: { orderBy: { field: 'date', order: 'asc' } }
    },
    { parseExtra: parseClassesDeepLinkFilters }
  );
  const { data, error, isLoading, isFetching } = useGetLessonsQuery(filters);
  const [scopes, setScopes] = useState<Scopes>({});
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const isAdminUser = currentUser?.isAdmin;

  const { data: venueOptionsData } = useGetVenuesQuery({
    data: { entries: 10000, orderBy: { field: 'title', order: 'asc' } }
  });

  let venueOptions: FormSelectOptions[] = [];

  if (venueOptionsData) {
    venueOptions = venueOptionsData.results.map((v) => {
      let ret: FormSelectOptions = {
        value: v.id,
        label: v.title
      };

      return ret;
    });
  }

  const filterFormFields: FilterField[] = [
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: (filters.data?.filterBy?.find((f) => f.field === 'id')?.value as string) ?? ''
    },
    {
      name: 'starts_on',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="starts_on"
          label="Date"
        />
      ),
      value:
        (filters.data?.filterBy?.find(
          (f) => f.field === 'date' && f.operator === 'greater_equal_than'
        )?.value as string) ?? ''
    },
    {
      name: 'ends_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="ends_on" />,
      value:
        (filters.data?.filterBy?.find(
          (f) => f.field === 'date' && f.operator === 'lesser_equal_than'
        )?.value as string) ?? ''
    },
    ...(currentUser?.isGlobal
      ? [{
          name: 'location_id',
          operator: 'equals' as const,
          element: (
            <FormSelect
              name="location_id"
              label="Location"
              options={locationOptions}
              selected_Value={locationOptions.find(
                (option) =>
                  String(option.value) ===
                  String(filters.data?.filterBy?.find((f) => f.field === 'location_id')?.value)
              )}
            />
          )
        }]
      : []),
    {
      name: 'course_type',
      element: (
        <FormSelect
          name="course_type"
          label="Session Type"
          options={courseTypeOptions}
          selected_Value={courseTypeOptions.find(
            (option) =>
              String(option.value) ===
              String(filters.data?.filterBy?.find((f) => f.field === 'course_type')?.value)
          )}
        />
      )
    },
    {
      name: 'course_title',
      label: 'Session Title',
      value:
        (filters.data?.filterBy?.find((f) => f.field === 'course_title')?.value as string) ?? ''
    },
    {
      name: 'venue_id',
      label: 'Venue ID',
      type: 'number',
      value: (filters.data?.filterBy?.find((f) => f.field === 'venue_id')?.value as string) ?? ''
    },
    {
      name: 'venue_title',
      label: 'Venue',
      type: 'text',
      value: (filters.data?.filterBy?.find((f) => f.field === 'venue_title')?.value as string) ?? ''
    },
    {
      name: 'venue_city',
      label: 'City',
      type: 'text',
      value: (filters.data?.filterBy?.find((f) => f.field === 'venue_city')?.value as string) ?? ''
    },
    {
      name: 'course_is_online',
      element: (
        <FormSelect
          name="course_is_online"
          label="Program Type"
          options={isOnlineOptions}
          selected_Value={isOnlineOptions.find(
            (option) =>
              String(option.value) ===
              String(filters.data?.filterBy?.find((f) => f.field === 'course_is_online')?.value)
          )}
        />
      )
    },
    {
      name: 'course_payment_type',
      element: (
        <FormSelect
          name="course_payment_type"
          label="Payment Type"
          options={paymentTypeOptions}
          selected_Value={paymentTypeOptions.find(
            (option) =>
              String(option.value) ===
              String(filters.data?.filterBy?.find((f) => f.field === 'course_payment_type')?.value)
          )}
        />
      )
    },
    {
      name: 'day_of_week',
      label: 'Day of Week',
      operator: 'equals',
      element: (
        <FormSelect
          name="day_of_week"
          label="Day of Week"
          options={dayOfWeekOptions}
          selected_Value={dayOfWeekOptions.find(
            (option) =>
              String(option.value) ===
              String(filters.data?.filterBy?.find((f) => f.field === 'day_of_week')?.value)
          )}
        />
      )
    },
    {
      name: 'main_instructor_full_name',
      label: 'Main Instructor',
      value:
        (filters.data?.filterBy?.find((f) => f.field === 'main_instructor_full_name')
          ?.value as string) ?? ''
    },
    {
      name: 'additional_instructor_full_name',
      label: 'Additional Instructor',
      value:
        (filters.data?.filterBy?.find((f) => f.field === 'additional_instructor_full_name')
          ?.value as string) ?? ''
    },
    {
      name: 'substitute_full_name',
      label: 'Substitute',
      value:
        (filters.data?.filterBy?.find((f) => f.field === 'substitute_full_name')?.value as string) ??
        ''
    },
    {
      name: 'observer_full_name',
      label: 'Observer',
      value:
        (filters.data?.filterBy?.find((f) => f.field === 'observer_full_name')?.value as string) ??
        ''
    },
    {
      name: 'trainee_full_name',
      label: 'Trainee',
      value:
        (filters.data?.filterBy?.find((f) => f.field === 'trainee_full_name')?.value as string) ?? ''
    },
    {
      name: 'is_recorded',
      element: (
        <FormSelect
          name="is_recorded"
          label="Is Recorded"
          options={affirmativeOptions}
          selected_Value={affirmativeOptions.find(
            (option) =>
              String(option.value) ===
              String(filters.data?.filterBy?.find((f) => f.field === 'is_recorded')?.value)
          )}
        />
      )
    }
  ];

  const sidebars = [<InstructorLegend key="legend" />];

  if (isAdminUser) {
    sidebars.unshift(
      <DetailSection
        key={'details'}
        title="Summary"
      >
        <LabelText
          label="# of Classes:"
          text={String(data?.count ?? 0)}
        />
        <LabelText
          label="# of Website Registrations:"
          text={String(data?.totals?.registrations ?? 0)}
        />
        <LabelText
          label="# of Onsite Enrollments:"
          text={String(data?.totals?.enrollments ?? 0)}
        />
      </DetailSection>
    );
  }

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setLessons(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    const transformedFilters = filterBy.map((filter) => {
      // Handle date range filters
      if (filter.field === 'starts_on') {
        return {
          operator: 'greater_equal_than',
          field: 'date',
          value: filter.value
        };
      } else if (filter.field === 'ends_on') {
        return {
          operator: 'lesser_equal_than',
          field: 'date',
          value: filter.value
        };
      }

      // Handle day_of_week capitalization
      if (filter.field === 'day_of_week') {
        return {
          ...filter,
          value: filter.value.charAt(0).toUpperCase() + filter.value.slice(1)
        };
      }

      return filter;
    });

    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy: transformedFilters
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Classes`}
        columns={classColumns}
        data={lessons}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        sidebars={sidebars}
        indexDownloadPath="lessons"
        filters={filters.data?.filterBy}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
      />
    </div>
  );
};
